import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Download, Trash2, Eye, Upload } from 'lucide-react';
import { BackupItem, RestoreOptions } from './types';
import { formatFileSize, formatDate, getOperationColor, getStatusIcon } from './utils';

interface BackupMobileListProps {
  backups: BackupItem[];
  selectedBackups: string[];
  onBackupSelect: (backupId: string) => void;
  onRestoreBackup: (backupId: string, options: RestoreOptions) => Promise<void>;
}

const BackupMobileList: React.FC<BackupMobileListProps> = ({
  backups,
  selectedBackups,
  onBackupSelect,
  onRestoreBackup,
}) => {
  const handleDownload = async (backupId: string, zoneName: string) => {
    try {
      const response = await fetch(`/api/backups/${backupId}/download`);
      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `backup-${zoneName}-${backupId}.json`;
        a.click();
        window.URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Failed to download backup:', error);
    }
  };

  const handleDelete = async (backupId: string) => {
    if (window.confirm('Are you sure you want to delete this backup?')) {
      try {
        const response = await fetch(`/api/backups/${backupId}`, { method: 'DELETE' });
        if (response.ok) {
          window.location.reload();
        }
      } catch (error) {
        console.error('Failed to delete backup:', error);
      }
    }
  };

  return (
    <div className="md:hidden space-y-4">
      {backups.map((backup) => (
        <motion.div
          key={backup.id}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white dark:bg-slate-800 p-4 rounded-lg shadow-sm border border-slate-200 dark:border-slate-700"
        >
          <div className="flex items-start justify-between mb-3">
            <div className="flex items-center space-x-3">
              <input
                type="checkbox"
                checked={selectedBackups.includes(backup.id)}
                onChange={() => onBackupSelect(backup.id)}
                className="rounded border-slate-300 dark:border-slate-600"
              />
              <div>
                <h3 className="font-medium text-slate-900 dark:text-slate-100">
                  {backup.zoneName}
                </h3>
                <p className="text-sm text-slate-500 dark:text-slate-400">{backup.provider}</p>
              </div>
            </div>
            <div className="flex items-center">
              {getStatusIcon(backup.status)}
              <span className="ml-1 text-sm text-slate-500 dark:text-slate-400 capitalize">
                {(backup.status ?? 'unknown').replace('_', ' ')}
              </span>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-4 text-sm">
            <div>
              <span className="text-slate-500 dark:text-slate-400">Operation:</span>
              <span
                className={`ml-2 px-2 py-1 text-xs rounded-full ${getOperationColor(backup.operation)}`}
              >
                {backup.operation}
              </span>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Size:</span>
              <span className="ml-2 text-slate-900 dark:text-slate-100">
                {formatFileSize(backup.fileSize)}
              </span>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Records:</span>
              <span className="ml-2 text-slate-900 dark:text-slate-100">{backup.recordCount}</span>
            </div>
            <div>
              <span className="text-slate-500 dark:text-slate-400">Created:</span>
              <span className="ml-2 text-slate-900 dark:text-slate-100">
                {formatDate(backup.timestamp)}
              </span>
            </div>
          </div>

          <div className="flex justify-end space-x-2">
            <button
              className="p-2 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/20 rounded transition-colors"
              title="View Details"
            >
              <Eye className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDownload(backup.id, backup.zoneName)}
              className="p-2 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded transition-colors"
              title="Download"
            >
              <Download className="w-4 h-4" />
            </button>
            <button
              onClick={() => onRestoreBackup(backup.id, {})}
              className="p-2 text-purple-600 dark:text-purple-400 hover:bg-purple-50 dark:hover:bg-purple-900/20 rounded transition-colors"
              title="Restore"
            >
              <Upload className="w-4 h-4" />
            </button>
            <button
              onClick={() => handleDelete(backup.id)}
              className="p-2 text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 rounded transition-colors"
              title="Delete"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </motion.div>
      ))}
    </div>
  );
};

export default BackupMobileList;
